import React, { useState, useEffect } from 'react';
import { useChat } from '../../hooks';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { ChatSidebar } from './ChatSidebar';

interface ChatContainerProps {
  className?: string;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({ className = '' }) => {
  const {
    sessions,
    currentSession,
    currentSessionId,
    isLoading,
    isStreaming,
    streamingContent,
    error,
    createSession,
    setCurrentSession,
    sendMessage,
    deleteSession,
  } = useChat();

  const [isMobile, setIsMobile] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const isMobileView = window.innerWidth < 1024;
      setIsMobile(isMobileView);
      if (!isMobileView) {
        setShowSidebar(true);
        setMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-create first session if no sessions exist
  useEffect(() => {
    if (sessions.length === 0 && !currentSessionId) {
      createSession('新对话');
    }
  }, [sessions.length, currentSessionId, createSession]);

  const handleCreateSession = () => {
    createSession('新对话');
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className={`flex h-full bg-white ${className}`}>
      {/* Desktop Sidebar */}
      {!isMobile && showSidebar && (
        <ChatSidebar
          sessions={sessions}
          currentSessionId={currentSessionId}
          onSelectSession={setCurrentSession}
          onCreateSession={handleCreateSession}
          onDeleteSession={deleteSession}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      {isMobile && mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Drawer */}
          <div className="fixed left-0 top-0 bottom-0 w-80 z-50 bg-white shadow-xl">
            <ChatSidebar
              sessions={sessions}
              currentSessionId={currentSessionId}
              onSelectSession={setCurrentSession}
              onCreateSession={handleCreateSession}
              onDeleteSession={deleteSession}
              isMobile={true}
              onClose={() => setMobileMenuOpen(false)}
            />
          </div>
        </>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            {isMobile && (
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="p-2 -ml-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-sm">业务AI助手</h2>
                <p className="text-xs text-gray-500">理解需求 · 生成内容</p>
              </div>
            </div>
          </div>

          {/* Desktop Toggle Sidebar Button */}
          {!isMobile && (
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title={showSidebar ? '隐藏侧边栏' : '显示侧边栏'}
            >
              <svg
                className={`w-5 h-5 transition-transform ${showSidebar ? '' : 'rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-4 py-2 bg-red-50 border-b border-red-100">
            <div className="flex items-center gap-2 text-sm text-red-600">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="flex-1">{error}</span>
              <button
                onClick={() => window.location.reload()}
                className="text-xs underline hover:no-underline"
              >
                刷新页面
              </button>
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-hidden bg-gray-50/50">
          {currentSession ? (
            <ChatMessageList
              messages={currentSession.messages}
              isStreaming={isStreaming}
              streamingContent={streamingContent}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  开始新的对话
                </h3>
                <p className="text-gray-500 mb-4">
                  点击左侧"新建对话"按钮开始
                </p>
                <button
                  onClick={handleCreateSession}
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:shadow-lg transition-all"
                >
                  新建对话
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 bg-white">
          <ChatInput
            onSend={sendMessage}
            disabled={isLoading || isStreaming}
            placeholder="描述你的需求，例如：创建一个Python编程入门课程页面..."
          />
        </div>
      </div>
    </div>
  );
};
